import { addDays } from 'date-fns'
import { merge, set } from 'lodash-es'
import { trackingParamsMap } from './trackingParams'
import type { RouteLocationNormalizedLoaded } from '#vue-router'

export function useEoTracking(partnerIds: StoryBlokPartnerId[], excludedReferrers: string[]) {
  const route = useRoute()
  const { public: { eoTracking: { referrerDomain } } } = useRuntimeConfig()
  const referrer = document?.referrer

  const excludedDomains = [!process.dev ? referrerDomain : 'localhost:3000', ...excludedReferrers]

  const hasQueryParams = Object.keys(route.query).some(key => trackingParamsMap.has(key))
  const hasWebReferrer = referrer?.includes(excludedDomains[0])
  const hasExcludedReferrer = excludedDomains.some(domain => referrer?.includes(domain))

  if (hasQueryParams || (!hasExcludedReferrer && (referrer !== undefined && referrer !== '')))
    setOrChangeTrackingCookies(hasQueryParams, hasWebReferrer, referrer, partnerIds, route)
}

function setOrChangeTrackingCookies(
  hasQueryParams: boolean,
  hasWebReferrer: boolean,
  referrer: string,
  partnerIds: StoryBlokPartnerId[],
  route: RouteLocationNormalizedLoaded,
) {
  const { public: { eoTracking: { cookieDomain, trackLoans, createCakeCookie } } } = useRuntimeConfig()
  const defaultPartnerId = ref<StoryBlokPartnerId | undefined>(undefined)
  if (!hasQueryParams)
    defaultPartnerId.value = partnerIds?.find(partnerId => referrer?.includes(partnerId.Referrer!))

  const eoTrackingCookieObject: StoryBlokPartnerId
    = setEoTrackingCookie(referrer, hasQueryParams, hasWebReferrer, route, defaultPartnerId.value)

  const cakeCookieObject: { [K: string]: string | null }
    = setCakeCookie(hasQueryParams, route, defaultPartnerId.value)

  for (const [key, value] of Object.entries(route.query)) {
    if (value && trackingParamsMap.has(key)) {
      if (key === 'utm_campaign')
        set(eoTrackingCookieObject, 'Channel', value)
      set(eoTrackingCookieObject, trackingParamsMap.get(key)!, value)
      set(cakeCookieObject, key, value)
    }
  }

  const eoTrackingCookie = useCookie('EOTracking', {
    expires: addDays(new Date(), 30),
    path: '/',
    domain: !process.dev ? cookieDomain : '',
  })

  if (createCakeCookie) {
    const cakeCookie = useCookie('CakeCookie[lead_params]', {
      expires: addDays(new Date(), 30),
      path: '/',
      domain: !process.dev ? cookieDomain : '',
    })
    cakeCookie.value = JSON.stringify(cakeCookieObject)
  }

  if (process.client && trackLoans) sendLoansTracking(eoTrackingCookieObject)

  eoTrackingCookie.value = JSON.stringify(eoTrackingCookieObject)
}

function setCakeCookie(
  hasQueryParams: boolean,
  route: RouteLocationNormalizedLoaded,
  defaultPartnerId?: StoryBlokPartnerId,
) {
  const cakeCookie = {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    utm_unique: null,
  }

  if (!hasQueryParams) {
    set(cakeCookie, 'utm_source', defaultPartnerId?.UtmSource || null)
    set(cakeCookie, 'utm_medium', defaultPartnerId?.UtmMedium || null)
    set(cakeCookie, 'utm_term', route?.path || null)
  }

  return cakeCookie
}

function setEoTrackingCookie(
  referrer: string,
  hasQueryParams: boolean,
  hasWebReferrer: boolean,
  route: RouteLocationNormalizedLoaded,
  defaultPartnerId?: StoryBlokPartnerId,
) {
  const eoTrackingCookie = {
    PartnerId: null,
    UtmSource: null,
    UtmMedium: null,
    UtmCampaign: null,
    UtmTerm: null,
    UtmContent: null,
    Channel: null,
    Referrer: hasWebReferrer ? '' : (referrer || null),
  }

  if (!hasQueryParams) {
    set(eoTrackingCookie, 'PartnerId', defaultPartnerId?.PartnerId || null)
    set(eoTrackingCookie, 'UtmSource', defaultPartnerId?.UtmSource || null)
    set(eoTrackingCookie, 'UtmMedium', defaultPartnerId?.UtmMedium || null)
    set(eoTrackingCookie, 'UtmCampaign', route?.path || null)
    set(eoTrackingCookie, 'Channel', route?.path || null)
  }

  return eoTrackingCookie
}

async function sendLoansTracking(trackingCookie: StoryBlokPartnerId) {
  const { public: { domain, eoTracking: { cookieDomain, loansTrackingUrl } } } = useRuntimeConfig()

  const loansData = Object.fromEntries(
    Object.entries(trackingCookie).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v]),
  )

  merge(loansData, loansData.hasOffersInfo)
  delete loansData.hasOffersInfo

  set(loansData, 'portal', `https://${domain}/`)
  set(loansData, 'gaClientId', useCookie('_ga')?.value)
  set(loansData, 'userAgent', navigator?.userAgent)
  set(loansData, 'id', useCookie('srt_tracking_session_id')?.value)

  $fetch<{ [K: string]: string | null }>(loansTrackingUrl, {
    headers: { 'content-type': 'application/json' },
    method: 'POST',
    cache: 'no-cache',
    mode: 'cors',
    body: JSON.stringify(loansData),
  }).then((response) => {
    if (response?.id) {
      useCookie('srt_tracking_session_id', {
        expires: addDays(new Date(), 30),
        path: '/',
        domain: !process.dev ? cookieDomain : '',
      }).value = response.id
    }
  })
}
